'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import sanitizeHtml from 'sanitize-html';
import { HowItWorksStep } from '../hiv-test/types';
import AnimatedGradientButton from './shared/AnimatedGradientButton';

const sanitizeAndRenderHtml = (
  content: string | undefined
): { __html: string } | undefined => {
  if (!content) return undefined;

  const sanitized = sanitizeHtml(content, {
    allowedAttributes: {
      span: ['class'],
    },
    allowedClasses: {
      span: ['*'], // Allow all classes on span elements
    },
    allowedTags: ['b', 'strong', 'i', 'em', 'u', 'span', 'br', 'p'],
  });

  return { __html: sanitized };
};

interface HowItWorksProps {
  sectionHeading?: string;
  steps?: HowItWorksStep[];
}

const HowItWorks = ({
  sectionHeading = 'How it Works',
  steps = [
    {
      description:
        "Shipped in packaging so discreet your nosy roommate won't say a word.",
      title: 'Order Online',
    },
    {
      description:
        'Follow our easy instructions in the comfort of your own bathroom.',
      title: 'Test at Home',
    },
    {
      description: 'Know your status in just one minute.',
      title: 'Get Your Results',
    },
    {
      buttonHref: '/sign-in',
      buttonText: 'Talk to a Nurse',
      description:
        'Have questions? Need help? Our judgment-free nurses are available over chat, phone, or video.',
      title: 'Talk to a Nurse',
    },
  ],
}: HowItWorksProps) => {
  const [howItWorksStep, setHowItWorksStep] = useState<number>(0);
  const howItWorksRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef<boolean>(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // After first render, scroll to the selected step when howItWorksStep changes
    if (
      howItWorksRef.current &&
      howItWorksRef.current.children[howItWorksStep]
    ) {
      howItWorksRef.current.children[howItWorksStep].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }
  }, [howItWorksStep]);

  return (
    <div className="flex w-full flex-col items-center px-6 py-12">
      <div className="content-wrapper w-full">
        <h2 className="align-middle font-['Spectral'] text-2xl leading-10 font-semibold tracking-[-5%] md:text-xl md:leading-[29px]">
          <span
            dangerouslySetInnerHTML={sanitizeAndRenderHtml(sectionHeading)}
          />
          {` (${howItWorksStep + 1}/${steps.length})`}
        </h2>
      </div>
      <section
        id="how-it-works"
        className="content-wrapper mt-6 flex w-full flex-col py-[48px] md:flex-row md:justify-between md:py-[24px]"
      >
        <div className="w-full flex-row">
          <div className="flex w-full flex-col overflow-x-hidden">
            <div
              className="mt-[24px] flex w-full gap-[24px] overflow-x-visible"
              ref={howItWorksRef}
            >
              {steps.map((step, i) => (
                <div
                  data-name={`how-it-works-step-${i}`}
                  className="flex h-[264px] min-w-full flex-col items-start justify-end rounded-[10px] bg-[url('/orange-purple-background.png')] bg-center p-[32px_28px] shadow-md md:h-[314px] md:min-w-[60%] md:p-[70px_28px]"
                  key={`how-it-works-step-${i}`}
                >
                  <h4
                    className="mb-[12px] align-middle font-['Work_Sans'] text-[32px] leading-[45px] font-normal tracking-[-5%] text-white md:text-[44px] md:leading-[60px]"
                    dangerouslySetInnerHTML={sanitizeAndRenderHtml(step.title)}
                  />
                  <span
                    className="align-middle font-['Work_Sans'] text-[14px] leading-[23px] font-normal tracking-[0%] text-white md:text-[16px] md:leading-[20px]"
                    dangerouslySetInnerHTML={sanitizeAndRenderHtml(
                      step.description
                    )}
                  />
                  {step.buttonText && step.buttonHref && (
                    <div className="mt-4">
                      <AnimatedGradientButton
                        href={step.buttonHref}
                        as="link"
                        theme="orange"
                        text={step.buttonText}
                        className="min-w-[140px]"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="content-wrapper mt-8 mb-4 flex w-full gap-2">
        <button
          className="transition-transform hover:scale-105 disabled:opacity-50"
          aria-label="Previous Step"
          disabled={howItWorksStep === 0}
          onClick={() => {
            if (howItWorksStep > 0) {
              setHowItWorksStep(howItWorksStep - 1);
            }
          }}
        >
          <Image
            height={40}
            width={40}
            alt="Previous step"
            src="/orange-gradient-left-icon.svg"
          />
        </button>
        <button
          className="transition-transform hover:scale-105 disabled:opacity-50"
          disabled={howItWorksStep === steps.length - 1}
          onClick={() => {
            if (howItWorksStep < steps.length - 1) {
              setHowItWorksStep(howItWorksStep + 1);
            }
          }}
        >
          <Image
            height={40}
            width={40}
            alt="Next step"
            src="/orange-gradient-right-icon.svg"
          />
        </button>
      </div>
    </div>
  );
};

export default HowItWorks;
